(() => {
  const ROOT_SELECTOR = '[data-cc-cart-upsell]';
  const ANCHOR_KEY = 'cc_cart_upsell_anchor_product';
  const PENDING_ANCHOR_KEY = 'cc_cart_upsell_pending_anchor_product';

  const storageGet = (key) => {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  };

  const storageSet = (key, value) => {
    try {
      if (value) sessionStorage.setItem(key, String(value));
      else sessionStorage.removeItem(key);
    } catch (e) {}
  };

  const cartProductIds = (root) => new Set(
    (root?.dataset?.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map(String)
  );

  const resolveAnchorProduct = (root) => {
    if (!root) return null;

    const ids = cartProductIds(root);
    const pending = storageGet(PENDING_ANCHOR_KEY);
    const stored = storageGet(ANCHOR_KEY);
    const fallback = root.dataset.productId ? String(root.dataset.productId) : null;

    let anchor = null;

    if (pending && ids.has(String(pending))) {
      anchor = String(pending);
      storageSet(ANCHOR_KEY, anchor);
      storageSet(PENDING_ANCHOR_KEY, null);
    } else if (stored && ids.has(String(stored))) {
      anchor = String(stored);
    } else if (fallback && ids.has(String(fallback))) {
      anchor = String(fallback);
      storageSet(ANCHOR_KEY, anchor);
    }

    if (!anchor) {
      storageSet(ANCHOR_KEY, null);
      storageSet(PENDING_ANCHOR_KEY, null);
    }

    return anchor;
  };

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: currency || 'HUF',
        maximumFractionDigits: currency === 'HUF' || !currency ? 0 : 2
      }).format((Number(cents) || 0) / 100);
    } catch (e) {
      return Math.round((Number(cents) || 0) / 100).toLocaleString('hu-HU') + ' Ft';
    }
  };

  const productUrl = (product) => {
    if (product.url) return product.url;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + 'products/' + product.handle;
  };

  const imageUrl = (product) => {
    const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : '');
    if (!image) return '';
    return typeof image === 'string' ? image : (image.src || image.url || '');
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const render = (root, products) => {
    if (!products.length) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }

    const currency = root.dataset.currency || 'HUF';

    root.innerHTML = `
      <div class="cc-cart-upsell__header">
        <h3 class="cc-cart-upsell__title">Ehhez jól jöhet</h3>
        <p class="cc-cart-upsell__subtitle">Kiegészítők a választott termékhez</p>
      </div>
      <div class="cc-cart-upsell__list">
        ${products.map((product) => {
          const availableVariants = (product.variants || []).filter((variant) => variant.available);
          const oneClickVariant = product.variants && product.variants.length === 1 && availableVariants.length === 1
            ? availableVariants[0]
            : null;
          const href = productUrl(product);
          const image = imageUrl(product);
          const price = product.price_min ?? product.price ?? oneClickVariant?.price ?? 0;

          return `
            <article class="cc-cart-upsell__item">
              <a href="${escapeHtml(href)}" class="cc-cart-upsell__media" aria-label="${escapeHtml(product.title)}">
                ${image ? `<img class="cc-cart-upsell__image" src="${escapeHtml(image)}" alt="" loading="lazy" width="116" height="116">` : ''}
              </a>
              <div class="cc-cart-upsell__info">
                <a href="${escapeHtml(href)}" class="cc-cart-upsell__name">${escapeHtml(product.title)}</a>
                <span class="cc-cart-upsell__price">${money(price, currency)}</span>
              </div>
              ${oneClickVariant
                ? `<button type="button" class="cc-cart-upsell__action" data-cc-upsell-add data-variant-id="${oneClickVariant.id}">+ Hozzáadás</button>`
                : `<a class="cc-cart-upsell__action" href="${escapeHtml(href)}">Válassz</a>`
              }
            </article>
          `;
        }).join('')}
      </div>
    `;

    root.hidden = false;
  };

  const loadRecommendations = async (root) => {
    if (!root || root.dataset.ccLoading === 'true') return;

    const productId = resolveAnchorProduct(root);
    const baseUrl = root.dataset.recommendationsUrl;

    if (!productId || !baseUrl) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }

    if (root.dataset.ccAnchorProduct !== String(productId)) {
      root.dataset.ccAnchorProduct = String(productId);
      root.dataset.ccLoaded = 'false';
    }

    if (root.dataset.ccLoaded === 'true') return;

    root.dataset.ccLoading = 'true';

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '6');
      url.searchParams.set('intent', 'complementary');

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Recommendation request failed');

      const payload = await response.json();
      const cartIds = new Set((root.dataset.cartProductIds || '').split(',').filter(Boolean).map(String));
      const products = (payload.products || [])
        .filter((product) => product.available && !cartIds.has(String(product.id)))
        .slice(0, 2);

      render(root, products);
      root.dataset.ccLoaded = 'true';
    } catch (error) {
      root.hidden = true;
      console.warn('CsakCsajok cart upsell:', error);
    } finally {
      delete root.dataset.ccLoading;
    }
  };

  const clearAnchorIfCartEmpty = () => {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (roots.length) return;
    const drawer = document.querySelector('#cartDrawer');
    if (drawer && !drawer.querySelector('.js-cart-items')) {
      storageSet(ANCHOR_KEY, null);
      storageSet(PENDING_ANCHOR_KEY, null);
    }
  };

  const refreshUpsells = () => {
    clearAnchorIfCartEmpty();
    document.querySelectorAll(ROOT_SELECTOR).forEach(loadRecommendations);
  };

  const updateHeaderCart = (sectionHtml) => {
    if (!sectionHtml) return;
    const current = document.querySelector('#headerCartStatus');
    if (!current) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const section = doc.querySelector('.shopify-section');
    current.innerHTML = section ? section.innerHTML : sectionHtml;
    current.classList.add('header-cart-status--animate');
  };

  const addUpsell = async (button) => {
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Hozzáadás…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', 'cart-drawer,header-cart-status');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: formData
      });

      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Nem sikerült hozzáadni a terméket.');
      }

      const drawerForm = document.querySelector('ecx-cart-drwr-frm');
      if (drawerForm && typeof drawerForm.renderContents === 'function' && payload.sections) {
        drawerForm.renderContents(payload);
      }

      if (payload.sections?.['header-cart-status']) {
        updateHeaderCart(payload.sections['header-cart-status']);
      }

      setTimeout(refreshUpsells, 80);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      console.warn('CsakCsajok cart upsell add:', error);
    }
  };

  const capturePersonalizedAnchor = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('js-main-product-form')) return;

    const productForm = form.closest('[data-cc-product-id]');
    const productId = productForm?.dataset?.ccProductId;
    if (!productId) return;

    storageSet(PENDING_ANCHOR_KEY, productId);
  };

  document.addEventListener('submit', capturePersonalizedAnchor, true);

  const captureStickyPersonalizedAnchor = (event) => {
    const button = event.target.closest('.cc-sticky-cart__button');
    if (!button || button.disabled) return;

    const productForm = button.closest('[data-cc-product-id]');
    const productId = productForm?.dataset?.ccProductId;
    if (!productId) return;

    storageSet(PENDING_ANCHOR_KEY, productId);
  };

  document.addEventListener('click', captureStickyPersonalizedAnchor, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cc-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addUpsell(button);
  });

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(refreshUpsells);
  });

  const start = () => {
    refreshUpsells();
    const drawer = document.querySelector('#cartDrawer');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
